import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Package, Search, RefreshCw, Truck, MapPin, 
  AlertTriangle, CheckCircle, Clock, QrCode, FileText,
  Download, Filter
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { QRCodeSVG } from 'qrcode.react';
import { getSystemDetailsUrl } from '../../lib/qrCode';
import LuggageTimeline from '../../components/LuggageTimeline';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const LuggageHistoryPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [luggage, setLuggage] = useState([]);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDestination, setFilterDestination] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [timelineRef, setTimelineRef] = useState(null);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const [luggageRes, stationsRes] = await Promise.all([
        axios.get(`${API_URL}/luggage`, { headers: { Authorization: `Bearer ${token}` }}),
        axios.get(`${API_URL}/stations`)
      ]);
      setLuggage(luggageRes.data);
      setStations(stationsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load luggage history');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status, pastDestination) => {
    if (pastDestination || status === 'past_destination') {
      return <Badge className="bg-red-500 text-white animate-pulse">⚠️ PAST DESTINATION</Badge>;
    }
    
    const statusConfig = {
      registered: { color: 'bg-blue-100 text-blue-700', icon: Clock },
      in_transit: { color: 'bg-yellow-100 text-yellow-700', icon: Truck },
      at_destination: { color: 'bg-green-100 text-green-700', icon: MapPin },
      delivered: { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
    };
    
    const config = statusConfig[status] || { color: 'bg-slate-100 text-slate-700', icon: Package };
    const Icon = config.icon;
    
    return (
      <Badge className={config.color}>
        <Icon className="h-3 w-3 mr-1" />
        {status?.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  const filteredLuggage = luggage.filter(item => {
    const matchesSearch = 
      item.luggage_ref?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.sender_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.receiver_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.sender_phone?.includes(searchTerm) ||
      item.receiver_phone?.includes(searchTerm);
    
    const matchesStatus = !filterStatus || item.status === filterStatus;
    const matchesDestination = !filterDestination ||
      item.destination_station_id === filterDestination;
    
    let matchesDate = true;
    if (dateFrom && item.created_at) {
      matchesDate = item.created_at >= dateFrom;
    }
    if (dateTo && item.created_at) {
      matchesDate = matchesDate && item.created_at <= dateTo + 'T23:59:59';
    }
    
    return matchesSearch && matchesStatus && matchesDestination && matchesDate;
  });

  // Stats
  const totalLuggage = filteredLuggage.length;
  const inTransit = filteredLuggage.filter(l => ['registered', 'loaded', 'in_transit', 'received'].includes(l.status)).length;
  const delivered = filteredLuggage.filter(l => ['delivered', 'claimed'].includes(l.status)).length;
  const pastDestination = filteredLuggage.filter(l => l.past_destination || l.status === 'past_destination').length;

  const exportToCSV = () => {
    const headers = ['Ref', 'Sender', 'Sender Phone', 'Receiver', 'Receiver Phone', 'Origin', 'Destination', 'Status', 'Created'];
    const rows = filteredLuggage.map(item => [
      item.luggage_ref,
      item.sender_name,
      item.sender_phone,
      item.receiver_name || '',
      item.receiver_phone || '',
      item.origin,
      item.destination,
      item.status,
      item.created_at ? format(parseISO(item.created_at), 'yyyy-MM-dd HH:mm') : ''
    ]);
    
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `luggage_history_${format(new Date(), 'yyyyMMdd')}.csv`;
    a.click();
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="luggage-history-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">
              Luggage History & Tracking
            </h1>
            <p className="text-slate-600">
              Track all luggage records and delivery status
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={exportToCSV}>
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="card-panashe">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Package className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{totalLuggage}</p>
                  <p className="text-sm text-slate-500">Total Luggage</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="card-panashe">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                  <Truck className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{inTransit}</p>
                  <p className="text-sm text-slate-500">In Transit</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="card-panashe">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{delivered}</p>
                  <p className="text-sm text-slate-500">Delivered</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className={`card-panashe ${pastDestination > 0 ? 'border-red-500 border-2' : ''}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${pastDestination > 0 ? 'bg-red-100' : 'bg-slate-100'}`}>
                  <AlertTriangle className={`h-5 w-5 ${pastDestination > 0 ? 'text-red-600' : 'text-slate-600'}`} />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${pastDestination > 0 ? 'text-red-600' : 'text-slate-900'}`}>{pastDestination}</p>
                  <p className="text-sm text-slate-500">Past Destination</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="card-panashe">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="md:col-span-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search by ref, name, or phone..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="search-luggage-input"
                  />
                </div>
              </div>
              
              <Select value={filterStatus || "all"} onValueChange={(val) => setFilterStatus(val === "all" ? "" : val)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="registered">Registered</SelectItem>
                  <SelectItem value="in_transit">In Transit</SelectItem>
                  <SelectItem value="at_destination">At Destination</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="past_destination">Past Destination</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterDestination || "all"} onValueChange={(val) => setFilterDestination(val === "all" ? "" : val)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Destinations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Destinations</SelectItem>
                  {stations.map(station => (
                    <SelectItem key={station.id} value={station.id}>
                      {station.name || station.city || station.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                placeholder="From Date"
              />
              
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                placeholder="To Date"
              />
            </div>
          </CardContent>
        </Card>

        {/* Luggage Table */}
        <Card className="card-panashe">
          <CardHeader className="border-b">
            <CardTitle className="font-heading flex items-center gap-2">
              <Package className="h-5 w-5" />
              Luggage Records ({filteredLuggage.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <RefreshCw className="h-8 w-8 animate-spin text-panashe-blue mx-auto mb-2" />
                <p className="text-slate-500">Loading luggage history...</p>
              </div>
            ) : filteredLuggage.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <Package className="h-12 w-12 mx-auto mb-2 opacity-30" />
                <p>No luggage records found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Ref / QR</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Sender</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Receiver</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Route</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Created</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {filteredLuggage.map(item => (
                      <tr 
                        key={item.id} 
                        className={`hover:bg-slate-50 ${item.past_destination || item.status === 'past_destination' ? 'bg-red-50' : ''}`}
                        data-testid={`luggage-row-${item.luggage_ref}`}
                      >
                        <td className="px-4 py-3">
                          <div className="flex flex-col items-center gap-2 min-w-[120px] max-w-[140px] text-center">
                            <div className="w-14 h-14 bg-white border rounded p-1 overflow-hidden shrink-0">
                              <QRCodeSVG
                                value={getSystemDetailsUrl(item.luggage_ref, 'luggage')}
                                size={46}
                                level="H"
                                includeMargin={false}
                                style={{ display: 'block', width: '100%', height: '100%' }}
                              />
                            </div>
                            <div className="w-full min-w-0">
                              <p className="font-mono font-bold text-[11px] break-all leading-tight">{item.luggage_ref}</p>
                              <p className="text-[10px] text-slate-500 whitespace-nowrap">Qty: {item.quantity || 1}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium">{item.sender_name}</p>
                          <p className="text-xs text-slate-500">{item.sender_phone}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium">{item.receiver_name || 'Self'}</p>
                          <p className="text-xs text-slate-500">{item.receiver_phone || '-'}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm">{item.origin} → {item.destination}</p>
                          {item.bus_registration && (
                            <p className="text-xs text-slate-500">Bus: {item.bus_registration}</p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {getStatusBadge(item.status, item.past_destination)}
                          {item.is_pay_forward && (
                            <Badge className="bg-orange-100 text-orange-700 ml-1">
                              💰 ${item.pay_forward_balance?.toFixed(2)}
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {item.created_at ? format(parseISO(item.created_at), 'dd MMM yyyy HH:mm') : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => navigate(`/luggage-ticket/${item.luggage_ref}`)}
                            >
                              <FileText className="h-4 w-4 mr-1" />
                              View
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setTimelineRef(item.luggage_ref)}
                              data-testid={`luggage-timeline-btn-${item.luggage_ref}`}
                            >
                              <Clock className="h-4 w-4 mr-1" />
                              Timeline
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <LuggageTimeline
        open={!!timelineRef}
        onClose={() => setTimelineRef(null)}
        luggageRef={timelineRef}
      />
    </DashboardLayout>
  );
};

export default LuggageHistoryPage;
